import { useCallback, useEffect, useRef, useState } from 'react';

import { fetchPhotos } from '../BackendRoute';

const DEFAULT_PER_PAGE = 5;
const DEFAULT_REFRESH_INTERVAL_MS = 30 * 1000; // 30 seconds

/**
 * Custom hook to manage photo feed data, pagination, and syncing via `after_uploaded_at`.
 *
 * @param {Object} options
 * @param {string} options.eventType - 'morning' or 'banquet'
 * @param {number} [options.perPage=5] - Number of photos to load per pagination page
 * @param {number} [options.refreshIntervalMs=30000] - Polling interval in ms
 * @param {boolean} [options.isPaused=false] - When true, auto-syncing is paused (e.g. during uploads)
 * @param {function} [options.onNewPhotos] - Callback invoked when new photos arrive during sync: (count) => void
 */
export function usePhotoFeed({
  eventType,
  perPage = DEFAULT_PER_PAGE,
  refreshIntervalMs = DEFAULT_REFRESH_INTERVAL_MS,
  isPaused = false,
  onNewPhotos,
}) {
  const [photos, setPhotos] = useState([]);
  const [hasNext, setHasNext] = useState(false);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState(null);

  // Keep refs to avoid stale closures in polling timers and prevent race conditions
  const photosRef = useRef(photos);
  useEffect(() => {
    photosRef.current = photos;
  }, [photos]);

  const loadingPhotosRef = useRef(loadingPhotos);
  useEffect(() => {
    loadingPhotosRef.current = loadingPhotos;
  }, [loadingPhotos]);

  const isPausedRef = useRef(isPaused);
  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  const isRefreshingRef = useRef(false);

  const onNewPhotosRef = useRef(onNewPhotos);
  useEffect(() => {
    onNewPhotosRef.current = onNewPhotos;
  }, [onNewPhotos]);

  // Load photos (initial load or pagination using before_uploaded_at)
  const loadPhotos = useCallback(
    async (beforeTimestamp = null, replace = false) => {
      setLoadingPhotos(true);
      try {
        const data = await fetchPhotos(eventType, perPage, beforeTimestamp);
        setPhotos((prev) =>
          replace ? data.photos : [...prev, ...data.photos],
        );
        setHasNext(Boolean(data.has_next));
        if (replace) {
          setLastRefreshedAt(new Date());
        }
      } catch (err) {
        console.error('Failed to load photos', err);
      } finally {
        setLoadingPhotos(false);
      }
    },
    [eventType, perPage],
  );

  // Initial load
  useEffect(() => {
    loadPhotos(null, true);
  }, [loadPhotos]);

  // Sync photos uploaded after the newest timestamp
  const syncPhotos = useCallback(async () => {
    if (
      isRefreshingRef.current ||
      loadingPhotosRef.current ||
      isPausedRef.current
    ) {
      return;
    }

    isRefreshingRef.current = true;
    setIsRefreshing(true);

    try {
      const currentPhotos = photosRef.current;
      if (currentPhotos.length === 0) {
        // If empty, perform an initial fetch
        const data = await fetchPhotos(eventType, perPage, null);
        setPhotos(data.photos || []);
        setHasNext(Boolean(data.has_next));
      } else {
        // Find highest uploaded_at timestamp among existing photos
        const latestTimestamp = currentPhotos.reduce(
          (max, p) => (p.uploaded_at > max ? p.uploaded_at : max),
          currentPhotos[0].uploaded_at,
        );

        // Fetch up to 50 photos uploaded after latestTimestamp
        const data = await fetchPhotos(eventType, 50, null, latestTimestamp);
        const incoming = data.photos || [];

        if (incoming.length > 0) {
          const currentIds = new Set(currentPhotos.map((p) => p.id));
          const trulyNew = incoming.filter((p) => !currentIds.has(p.id));

          if (trulyNew.length > 0) {
            setPhotos((prev) => {
              const prevIds = new Set(prev.map((p) => p.id));
              const itemsToAdd = trulyNew.filter((p) => !prevIds.has(p.id));
              if (itemsToAdd.length === 0) return prev;
              return [...itemsToAdd, ...prev];
            });

            if (onNewPhotosRef.current) {
              onNewPhotosRef.current(trulyNew.length);
            }
          }
        }
      }
      setLastRefreshedAt(new Date());
    } catch (err) {
      console.error('Failed to sync photos', err);
    } finally {
      isRefreshingRef.current = false;
      setIsRefreshing(false);
    }
  }, [eventType, perPage]);

  // Auto-refresh interval with Page Visibility optimization
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncPhotos();
      }
    };

    const intervalId = setInterval(() => {
      if (document.visibilityState === 'visible') {
        syncPhotos();
      }
    }, refreshIntervalMs);

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [syncPhotos, refreshIntervalMs]);

  // Pagination handler
  const loadMore = useCallback(() => {
    const currentPhotos = photosRef.current;
    const lastPhoto = currentPhotos[currentPhotos.length - 1];
    loadPhotos(lastPhoto ? lastPhoto.uploaded_at : null);
  }, [loadPhotos]);

  // Locally prepend photo (e.g. after successful upload)
  const addPhoto = useCallback((newPhoto) => {
    setPhotos((prev) => [newPhoto, ...prev]);
    setLastRefreshedAt(new Date());
  }, []);

  return {
    photos,
    hasNext,
    loadingPhotos,
    isRefreshing,
    lastRefreshedAt,
    loadPhotos,
    loadMore,
    syncPhotos,
    addPhoto,
  };
}
